/**
 * Page détail/modification d'une Leçon
 *
 * - Mode lecture : affiche les détails + liste des exercices
 * - Mode édition : formulaire inline
 * - Navigation vers exercices
 */

import React, { useState, useCallback } from 'react';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Breadcrumbs,
  Link,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Add, Edit, Home, Close, Quiz } from '@mui/icons-material';
import { Lecon } from '../../types';
import ChapitresLeconsApiService from '../../services/chapitresLeconsApi';
import CurriculumApiService from '../../services/curriculumApi';
import ExercicesApiService from '../../services/exercicesApi';
import LeconForm from './LeconForm';
import LeconDetail from './LeconDetail';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRBAC } from '../../contexts/RBACContext';
import { useDeleteExercice } from '../../hooks/useData';
import { useNotify } from '../../contexts/NotificationContext';
import { adresseExplorateur } from '../../features/contenu/adresses';

const LeconView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Seul un admin modifie le contenu.
  const { isAdmin } = useRBAC();
  const peutModifier = isAdmin();

  const [isEditing, setIsEditing] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [exerciceToDelete, setExerciceToDelete] = useState<string | null>(null);
  const { notify } = useNotify('content');
  const { environment: env } = useEnvironment();

  const deleteExerciceMutation = useDeleteExercice();

  // Charger la leçon
  const { data: lecon, isLoading: loadingLecon } = useQuery({
    queryKey: ['lecon', id, env],
    queryFn: () => ChapitresLeconsApiService.getLeconById(env, id!),
    enabled: !!id
  });

  // Charger le chapitre parent
  const { data: chapitre } = useQuery({
    queryKey: ['chapitre', lecon?.chapitreId, env],
    queryFn: () => ChapitresLeconsApiService.getChapitreById(env, lecon!.chapitreId!),
    enabled: !!lecon?.chapitreId
  });

  // Charger la matière parente
  const { data: matiere } = useQuery({
    queryKey: ['matiere', chapitre?.matiereId, env],
    queryFn: () => CurriculumApiService.getMatiereById(env, chapitre!.matiereId!),
    enabled: !!chapitre?.matiereId
  });

  // Charger la classe parente
  const { data: classe } = useQuery({
    queryKey: ['classe', matiere?.classeId, env],
    queryFn: () => CurriculumApiService.getClasseById(env, matiere!.classeId!),
    enabled: !!matiere?.classeId
  });

  // Charger les exercices de cette leçon
  const { data: allExercices = [] } = useQuery({
    queryKey: ['exercices', env],
    queryFn: () => ExercicesApiService.getExercices(env)
  });

  const exercices = allExercices.filter(e => e.leconId === id);

  const handleSave = useCallback(async (data: Omit<Lecon, 'id'>) => {
    if (!id) return;

    try {
      await ChapitresLeconsApiService.updateLecon(env, id, data);
      notify('Leçon mise à jour avec succès', 'success');
      setIsEditing(false);
      // Invalider le cache
      queryClient.invalidateQueries({ queryKey: ['lecon', id] });
      queryClient.invalidateQueries({ queryKey: ['lecons'] });
    } catch {
      notify('Erreur lors de la sauvegarde', 'error');
    }
  }, [id, env, queryClient, notify]);

  const handleDeleteExercice = async (exerciceId: string) => {
    try {
      await deleteExerciceMutation.mutateAsync({ id: exerciceId, env });
      notify('Exercice supprimé avec succès', 'success');
    } catch {
      notify('Erreur lors de la suppression', 'error');
    }
    setOpenDelete(false);
    setExerciceToDelete(null);
  };

  const handleExerciceClick = (exerciceId: string) => {
    navigate(`/exercices/${exerciceId}/edit-advanced`);
  };

  const columns: GridColDef[] = [
    {
      field: 'titre',
      headerName: 'Titre',
      width: 250,
      renderCell: (params) => (
        <Link
          component="button"
          variant="body2"
          onClick={() => handleExerciceClick(params.row.id)}
          sx={{ cursor: 'pointer', textAlign: 'left' }}
        >
          {params.value}
        </Link>
      )
    },
    {
      field: 'ordre',
      headerName: 'Ordre',
      width: 80
    },
    {
      field: 'niveau',
      headerName: 'Niveau',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value || 'N/A'}
          color={params.value === 'facile' ? 'success' : params.value === 'moyen' ? 'warning' : 'error'}
          size="small"
        />
      )
    },
    {
      field: 'questions',
      headerName: 'Questions',
      width: 120,
      renderCell: (params) => (
        <Chip
          icon={<Quiz />}
          label={params.value?.length || 0}
          color="info"
          size="small"
        />
      )
    },
    {
      field: 'tempsEstimeMinutes',
      headerName: 'Durée (min)',
      width: 120
    },
    {
      field: 'statut',
      headerName: 'Statut',
      width: 120,
      renderCell: (params) => {
        const statut = params.value || 'published';
        return (
          <Chip
            label={statut === 'draft' ? 'Brouillon' : 'Publié'}
            color={statut === 'draft' ? 'warning' : 'success'}
            size="small"
          />
        );
      }
    },
    {
      field: 'estActif',
      headerName: 'Actif',
      width: 80,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Oui' : 'Non'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    }
  ];

  if (loadingLecon) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!lecon) {
    return (
      <Box textAlign="center" mt={4}>
        <Typography variant="h5">Leçon non trouvée</Typography>
        <Button onClick={() => navigate(adresseExplorateur())} sx={{ mt: 2 }}>
          Retour aux leçons
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/')}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          <Home sx={{ mr: 0.5 }} fontSize="small" />
          Accueil
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate(adresseExplorateur({ chapitre: lecon?.chapitreId, ouvert: lecon?.id }))}
          sx={{ cursor: 'pointer' }}
        >
          Leçons
        </Link>
        {classe && (
          <Link
            component="button"
            variant="body1"
            onClick={() => navigate(`/classes/${classe.id}`)}
            sx={{ cursor: 'pointer' }}
          >
            {classe.nom}
          </Link>
        )}
        {matiere && (
          <Link
            component="button"
            variant="body1"
            onClick={() => navigate(`/matieres/${matiere.id}`)}
            sx={{ cursor: 'pointer' }}
          >
            {matiere.nom}
          </Link>
        )}
        {chapitre && (
          <Link
            component="button"
            variant="body1"
            onClick={() => navigate(`/chapitres/${chapitre.id}`)}
            sx={{ cursor: 'pointer' }}
          >
            {chapitre.titre}
          </Link>
        )}
        <Typography color="text.primary">{lecon.titre}</Typography>
      </Breadcrumbs>

      {/* Informations de la leçon */}
      <Paper sx={{ mb: 3, p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Typography variant="h5" component="h1">
            {lecon.titre}
          </Typography>
          {peutModifier && !isEditing && (
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={() => setIsEditing(true)}
            >
              Modifier
            </Button>
          )}
          {isEditing && (
            <Button
              variant="outlined"
              startIcon={<Close />}
              onClick={() => setIsEditing(false)}
            >
              Annuler
            </Button>
          )}
        </Box>

        {isEditing ? (
          <LeconForm
            lecon={lecon}
            defaultChapitreId={lecon.chapitreId}
            onSubmit={handleSave}
            onCancel={() => setIsEditing(false)}
          />
        ) : (
          <LeconDetail lecon={lecon} />
        )}
      </Paper>

      <Divider sx={{ my: 3 }} />

      {/* Liste des exercices */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          Exercices de cette leçon ({exercices.length})
        </Typography>
        {peutModifier && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate(`/exercices/new?leconId=${id}&chapitreId=${lecon.chapitreId}`)}
          >
            Nouvel Exercice
          </Button>
        )}
      </Box>

      <Paper sx={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={exercices}
          columns={columns}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          disableRowSelectionOnClick
        />
      </Paper>

      {/* Dialog de suppression */}
      {peutModifier && (
        <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
          <DialogTitle>Confirmer la suppression</DialogTitle>
          <DialogContent>
            <Typography>
              Êtes-vous sûr de vouloir supprimer cet exercice ? Cette action est irréversible.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDelete(false)}>Annuler</Button>
            <Button
              onClick={() => exerciceToDelete && handleDeleteExercice(exerciceToDelete)}
              color="error"
              variant="contained"
            >
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>
      )}

    </Box>
  );
};

export default LeconView;
